import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Link } from 'react-router-dom';
import './App.css'; // Ensure this CSS loads early enough to avoid any flash

// Lazy load all components
const Home = React.lazy(() => import('./components/Home'));
const Features = React.lazy(() => import('./components/Features'));
const Products = React.lazy(() => import('./components/Products'));
const About = React.lazy(() => import('./components/About'));
const Blog = React.lazy(() => import('./components/Blog'));
const RequestDemo = React.lazy(() => import('./components/RequestDemo'));
const NewsAndSentiment = React.lazy(() => import('./features/NewsAndSentiment/NewsAndSentiment'));
const DataAnalytics = React.lazy(() => import('./features/DataAnalytics/DataAnalytics'));
const TextAndVideoInsights = React.lazy(() => import('./features/TextAndVideoInsights/TextAndVideoInsights'));
const AIBasedSignals = React.lazy(() => import('./features/AIBasedSignals/AIBasedSignals'));
const InterconnectedIntelligence = React.lazy(() => import('./features/InterconnectedIntelligence /InterconnectedIntelligence'));
const AutonomousInvestmentAgents = React.lazy(() => import('./features/AutonomousInvestmentAgents/AutonomousInvestmentAgents'));
const ExtensiveDashboards = React.lazy(() => import('./features/ExtensiveDashboards/ExtensiveDashboards'));
const ReportStudio = React.lazy(() => import('./features/ReportStudio/ReportStudio'));
const SearchEngine = React.lazy(() => import('./features/SearchEngine/SearchEngine'));
const AskFina = React.lazy(() => import('./features/AskFina/AskFina'));
const DemoVideo = React.lazy(() => import('./components/DemoVideo'));
const CaseStudy = React.lazy(() => import('./components/CaseStudy'));
const NewsletterApril2026 = React.lazy(() => import('./components/NewsletterApril2026'));

const Header = ({ currentSection }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      const headerOffset = 50;
      const elementPosition = section.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileMenuOpen(false);
    } else if (id === 'request-demo') {
      // Navigate to request-demo page if not on main page
      window.location.href = '/request-demo';
    } else {
      // Navigate to home page with hash
      window.location.href = `/#${id}`;
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header>
      <div className="header-content">
        <div className="logo-container" onClick={() => window.location.href = '/'}>
          <span className="logo-text" style={{ cursor: 'pointer' }}>
            Fina<span className="logo-signal">Signal</span>
          </span>
        </div>

        <nav>
          <ul className="desktop-nav">
            <li
              onClick={() => scrollToSection('home')}
              className={currentSection === 'home' ? 'active' : ''}
            >
              Home
            </li>
            <li
              onClick={() => scrollToSection('products')}
              className={currentSection === 'products' ? 'active' : ''}
            >
              Products
            </li>
            <li
              onClick={() => scrollToSection('features')}
              className={currentSection === 'features' ? 'active' : ''}
            >
              FinaSignal at Work
            </li>
            <li
              onClick={() => scrollToSection('about')}
              className={currentSection === 'about' ? 'active' : ''}
            >
              Top Challenges
            </li>
            <li
              onClick={() => scrollToSection('request-demo')}
              className={`nav-request-demo ${currentSection === 'request-demo' ? 'active' : ''}`}
            >
              Request Demo
            </li>
            <li>
              <a
                href="https://www.fina-ai.org/"
                className="client-login-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                Client Login
              </a>
            </li>
          </ul>

          <div 
            className="mobile-nav" 
            onClick={toggleMobileMenu}
            role="button"
            aria-label="Toggle navigation menu"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleMobileMenu();
              }
            }}
          >
            ☰
          </div>

          {isMobileMenuOpen && (
            <ul className="mobile-menu">
              <li onClick={() => scrollToSection('home')}>Home</li>
              <li onClick={() => scrollToSection('products')}>Products</li>
              <li onClick={() => scrollToSection('features')}>FinaSignal at Work</li>
              <li onClick={() => scrollToSection('about')}>Top Challenges</li>
              <li onClick={() => scrollToSection('request-demo')}>Request Demo</li>
              <li onClick={() => window.open('https://www.fina-ai.org/', '_blank', 'noopener,noreferrer')}>Client Login</li>
            </ul>
          )}
        </nav>
      </div>
    </header>
  );
};

const Footer = () => (
  <footer>
    <div className="footer-content">
      <div className="footer-grid">
        <div className="footer-brand">
          <span className="logo-text">Fina<span className="logo-signal">Signal</span></span>
          <p className="footer-tagline">Deep analytics for smarter decisions</p>
        </div>
        <div className="footer-col">
          <h4 className="footer-col-title">Platform</h4>
          <a href="/#products" className="footer-link">Products</a>
          <a href="/#features" className="footer-link">FinaSignal at Work</a>
          <a href="/#about" className="footer-link">Top Challenges</a>
        </div>
        <div className="footer-col">
          <h4 className="footer-col-title">Connect</h4>
          <a href="/#request-demo" className="footer-link">Request Demo</a>
          <a href="https://www.fina-ai.org/" className="footer-link" target="_blank" rel="noopener noreferrer">Client Login</a>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; 2026 FinaSignal. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

const MainContent = () => (
  <>
    <Home />
    <Products />
    <Features />
    <About />
    <RequestDemo />
  </>
);

const FeaturesLinks = () => (
  <div id="features">
    <h2>Features</h2>
    <ul>
      <li>
        <Link to="/features/current-affair-radar">Live Themes</Link>
      </li>
      <li>
        <Link to="/features/research-agents">Research Analyst</Link>
      </li>
      <li>
        <Link to="/features/intelligent-search">Intelligent Search</Link>
      </li>
      <li>
        <Link to="/features/data-analytic-agents">Data Analytic Agents</Link>
      </li>
      <li>
        <Link to="/features/nontraditional-data-explorer">Unstructured Data Explorer</Link>
      </li>
      <li>
        <Link to="/features/advanced-signals">Advanced Signals</Link>
      </li>
      <li>
        <Link to="/features/instant-reports">Instant Reports</Link>
      </li>
      <li>
        <Link to="/features/dashboards-and-insights">Entity Deepdive</Link>
      </li>
      <li>
        <Link to="/features/interconnected-intelligence">Interconnected Intelligence</Link>
      </li>
    </ul>
  </div>
);

const App = () => {
  const [currentSection, setCurrentSection] = useState('');

  const handleScroll = useCallback(() => {
    const sections = document.querySelectorAll('section');
    const scrollPosition = window.scrollY + 150; // Header height offset
    
    let foundSection = '';
    
    // Check sections from bottom to top to prioritize lower sections when overlapping
    const sectionsArray = Array.from(sections).reverse();
    
    for (const section of sectionsArray) {
      const offsetTop = section.offsetTop;
      const offsetHeight = section.offsetHeight;
      
      // Check if scroll position is within this section
      if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
        foundSection = section.getAttribute('id');
        break;
      }
    }
    
    // If no section found, find the closest one
    if (!foundSection) {
      let minDistance = Infinity;
      sections.forEach((section) => {
        const offsetTop = section.offsetTop;
        const distance = Math.abs(scrollPosition - offsetTop);
        
        if (distance < minDistance) {
          minDistance = distance;
          foundSection = section.getAttribute('id');
        }
      });
    }

    if (foundSection) {
      setCurrentSection(prevSection => {
        if (prevSection !== foundSection) {
          return foundSection;
        }
        return prevSection;
      });
    }
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in');
        }
      });
    }, observerOptions);

    // Observe all sections
    const sections = document.querySelectorAll('section');
    sections.forEach((section) => {
      observer.observe(section);
    });

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [handleScroll]);

  return (
    <Router>
      <div className="App">
        <Header currentSection={currentSection} />
        <Suspense fallback={<div className="loader-container"><div className="loader"></div></div>}>
          <Routes>
            <Route path="/" element={<MainContent />} />
            <Route path="/features" element={<FeaturesLinks />} />
            <Route path="/features/current-affair-radar" element={<NewsAndSentiment />} />
            <Route path="/features/data-analytic-agents" element={<DataAnalytics />} />
            <Route path="/features/nontraditional-data-explorer" element={<TextAndVideoInsights />} />
            <Route path="/features/advanced-signals" element={<AIBasedSignals />} />
            <Route path="/features/interconnected-intelligence" element={<InterconnectedIntelligence />} />
            <Route path="/features/research-agents" element={<AutonomousInvestmentAgents />} />
            <Route path="/features/ask-fina" element={<AskFina />} />
            <Route path="/features/dashboards-and-insights" element={<ExtensiveDashboards />} />
            <Route path="/features/instant-reports" element={<ReportStudio />} />
            <Route path="/features/intelligent-search" element={<SearchEngine />} />
            <Route path="/about" element={<About />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/request-demo" element={<RequestDemo />} />
            <Route path="/demo" element={<DemoVideo />} />
            <Route path="/case_study" element={<CaseStudy />} />
            <Route path="/newsletters/april-2026" element={<NewsletterApril2026 />} />
          </Routes>
        </Suspense>
        <Footer />
      </div>
    </Router>
  );
};

export default App;
